import { useEffect, useState } from "react";
import { PageHeader } from "@/shared/components/PageHeader";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { useAuth } from "@/shared/auth/useAuth";
import {
  useAssistantSettings,
  useAssistantUsage,
  useUpdateAssistantSettings,
} from "./use-assistant";
import type { AutonomyLevel, BudgetMode } from "./types";

interface Form {
  enabled: boolean;
  autonomyLevel: AutonomyLevel;
  monthlyBudgetUsd: number;
  budgetMode: BudgetMode;
  warnThresholdPct: number;
  stepCap: number;
}

/**
 * Assistant admin (ADR-0017): per-tenant autonomy + budget, plus this month's
 * usage. Editable by a platform SUPERADMIN; others see it read-only (the API
 * also rejects a non-superadmin PUT).
 */
export function AssistantSettingsPage() {
  const { user } = useAuth();
  const canEdit = !!user?.superadmin;
  const settings = useAssistantSettings();
  const usage = useAssistantUsage();
  const update = useUpdateAssistantSettings();

  const [form, setForm] = useState<Form | null>(null);
  useEffect(() => {
    if (settings.data && !form) {
      const s = settings.data;
      setForm({
        enabled: s.enabled,
        autonomyLevel: s.autonomyLevel,
        monthlyBudgetUsd: s.monthlyBudgetUsd,
        budgetMode: s.budgetMode,
        warnThresholdPct: s.warnThresholdPct,
        stepCap: s.stepCap,
      });
    }
  }, [settings.data, form]);

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Assistant settings"
        footnote="Per-workspace autonomy and monthly budget for the AI assistant, plus this month's spend."
      />

      {usage.data && (
        <KpiStrip>
          <KpiTile label="Spent this month" value={`$${usage.data.budget.spentUsd.toFixed(2)}`} />
          <KpiTile label="Budget" value={`$${usage.data.budget.limitUsd.toFixed(2)}`} highlighted />
          <KpiTile label="Your spend" value={`$${usage.data.yourSpendUsd.toFixed(2)}`} />
          <KpiTile label="Mode" value={usage.data.budget.mode} />
        </KpiStrip>
      )}

      {!canEdit && (
        <p className="mt-6 text-[13px] text-ge-muted" data-testid="assistant-settings-readonly">
          Only a platform admin can change these. You&apos;re viewing them read-only.
        </p>
      )}

      {form && (
        <Card className="mt-6 space-y-5 p-5">
          <Row label="Enabled" hint="Turn the assistant on or off for this workspace.">
            <Toggle checked={form.enabled} disabled={!canEdit} onChange={(v) => set("enabled", v)} testId="set-enabled" />
          </Row>

          <Row label="Autonomy" hint="Copilot asks before acting; autonomous chains steps within the cap. (v1 is read-only.)">
            <Select
              value={form.autonomyLevel}
              disabled={!canEdit}
              testId="set-autonomy"
              options={["OFF", "COPILOT", "AUTONOMOUS"]}
              onChange={(v) => set("autonomyLevel", v as AutonomyLevel)}
            />
          </Row>

          <Row label="Monthly budget (USD)" hint="Spend cap per calendar month.">
            <Input
              type="number"
              min={0}
              data-testid="set-budget"
              disabled={!canEdit}
              value={form.monthlyBudgetUsd}
              onChange={(e) => set("monthlyBudgetUsd", Math.max(0, Number(e.target.value) || 0))}
              className="w-32"
            />
          </Row>

          <Row label="Budget mode" hint="Soft warns and continues; hard blocks at 100%.">
            <Select
              value={form.budgetMode}
              disabled={!canEdit}
              testId="set-mode"
              options={["SOFT", "HARD"]}
              onChange={(v) => set("budgetMode", v as BudgetMode)}
            />
          </Row>

          <Row label="Warn at (%)" hint="Show a warning once spend crosses this share of the budget.">
            <Input
              type="number"
              min={1}
              max={100}
              data-testid="set-warn"
              disabled={!canEdit}
              value={form.warnThresholdPct}
              onChange={(e) => set("warnThresholdPct", Number(e.target.value) || 0)}
              className="w-24"
            />
          </Row>

          <Row label="Step cap" hint="Max tool calls per question (safety + cost).">
            <Input
              type="number"
              min={1}
              max={50}
              data-testid="set-stepcap"
              disabled={!canEdit}
              value={form.stepCap}
              onChange={(e) => set("stepCap", Number(e.target.value) || 1)}
              className="w-24"
            />
          </Row>

          {canEdit && (
            <div className="flex items-center gap-3 pt-1">
              <Button
                data-testid="set-save"
                disabled={update.isPending}
                onClick={() => update.mutate(form)}
              >
                {update.isPending ? "Saving…" : "Save changes"}
              </Button>
              {update.isSuccess && <span className="text-[12px] text-ge-accent">Saved.</span>}
              {update.isError && <span className="text-[12px] text-ge-danger">Couldn&apos;t save.</span>}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Row({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-ge-primary">{label}</p>
        <p className="text-[12px] text-ge-secondary">{hint}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Select({
  value,
  options,
  disabled,
  onChange,
  testId,
}: {
  value: string;
  options: string[];
  disabled?: boolean;
  onChange: (v: string) => void;
  testId?: string;
}) {
  return (
    <select
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-ge-param border border-ge-border-card bg-ge-card px-2 py-1 font-mono text-[12px] text-ge-value focus-visible:outline-none disabled:opacity-50"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  testId,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-[22px] w-[38px] items-center rounded-full px-[3px] transition-colors disabled:opacity-50 ${checked ? "bg-ge-accent" : "bg-ge-border-card"}`}
    >
      <span className={`h-[16px] w-[16px] rounded-full bg-white transition-transform ${checked ? "translate-x-[16px]" : ""}`} />
    </button>
  );
}
